import { combineReducers } from 'redux';
import { ActionType } from 'typesafe-actions';
import { dashboardActions } from './actions';
import {
  TIPSTER_FEE_TIERS_FAILURE,
  TIPSTER_FEE_TIERS_REQUEST,
  TIPSTER_FEE_TIERS_SUCCESS,
  GET_SALES_REQUEST,
  GET_SALES_SUCCESS,
  GET_STATS_REQUEST,
  GET_STATS_SUCCESS,
  GET_STATS_FAILURE,
  DOWNLOAD_ALL_CSV_REQUEST,
  DOWNLOAD_ALL_CSV_SUCCESS,
  DOWNLOAD_ALL_CSV_FAILURE,
  PUT_PAYOUTS_METHOD_REQUEST,
  PUT_PAYOUTS_METHOD_SUCCESS,
  SALES_NEXT_PAGE_SUCCESS,
  GET_BALANCE_REQUEST,
  GET_BALANCE_SUCCESS,
  BALANCE_NEXT_PAGE_SUCCESS,
  UPDATE_BALANCE_OPTIMISTIC,
  ADD_TRANSACTION_OPTIMISTIC,
  REMOVE_TRANSACTION_OPTIMISTIC,
} from '../../constants/dashboard';
import { Summary, PayoutsSummary } from '../../types/Earnings';
import { isEmpty } from 'lodash';
import { BalancePayoutMethodFromAPI } from '../../sagas/dashboard/types';

export type DashboardState = {
  requested: boolean;
  tipsterFeeTiers: any;
  salesSummary: any;
  salesTransactions: any;
  groupedSales: any;
  balanceSummary: any;
  balanceTransactions: any;
  balanceNextCursor: string | null;
  balanceHasMore: boolean;
  statsSummary: any;
  allCsvSummary: any;
  isLoading: boolean;
  isLoadingCsv: boolean;
  error: string | null;
};
export type DashboardActions = ActionType<typeof dashboardActions>;

export const dashboardReducer = combineReducers<
  DashboardState,
  DashboardActions
>({
  requested: (state = false, action) => {
    switch (action.type) {
      case GET_SALES_REQUEST:
      case GET_STATS_REQUEST:
      case GET_BALANCE_REQUEST:
      case TIPSTER_FEE_TIERS_REQUEST:
      case DOWNLOAD_ALL_CSV_REQUEST:
        return true;

      default:
        return state;
    }
  },

  balanceSummary: (state = {}, action) => {
    switch (action.type) {
      case GET_BALANCE_SUCCESS:
        const summary = action.payload.summary;
        return summary;
      case PUT_PAYOUTS_METHOD_SUCCESS:
        const payoutMethod: BalancePayoutMethodFromAPI =
          action.payload.payout_method;

        const updatedState = {
          ...state,
          payoutMethod: {
            bankAddress: payoutMethod.bank_address,
            bankName: payoutMethod.bank_name,
            clabe: payoutMethod.clabe,
            swiftCode: payoutMethod.swift_code,
            type: payoutMethod.type,
            value: payoutMethod.value,
          },
        };
        return updatedState;
      case UPDATE_BALANCE_OPTIMISTIC:
        // Actualización optimista del balance
        return {
          ...state,
          ...action.payload,
        };
      default:
        return state;
    }
  },
  balanceTransactions: (state = [], action) => {
    switch (action.type) {
      case GET_BALANCE_SUCCESS:
        const transactions = action.payload.balances;
        return transactions;
      case BALANCE_NEXT_PAGE_SUCCESS:
        const transactionsList = isEmpty(state)
          ? [...action.payload.balances]
          : [...state, ...action.payload.balances];
        return transactionsList;
      case ADD_TRANSACTION_OPTIMISTIC:
        // Agregar transacción al inicio de la lista (más reciente primero)
        return [action.payload, ...state];
      case REMOVE_TRANSACTION_OPTIMISTIC:
        // Remover transacción por ID
        return state.filter((transaction: any) => transaction.ID !== action.payload);
      default:
        return state;
    }
  },
  balanceNextCursor: (state = null, action) => {
    switch (action.type) {
      case GET_BALANCE_SUCCESS:
      case BALANCE_NEXT_PAGE_SUCCESS:
        return action.payload.nextCursor || null;
      default:
        return state;
    }
  },
  balanceHasMore: (state = false, action) => {
    switch (action.type) {
      case GET_BALANCE_SUCCESS:
      case BALANCE_NEXT_PAGE_SUCCESS:
        return action.payload.hasMore || false;
      default:
        return state;
    }
  },

  tipsterFeeTiers: (state = {}, action) => {
    switch (action.type) {
      case TIPSTER_FEE_TIERS_SUCCESS:
        const tipsterFeeTiers = action.payload;
        return tipsterFeeTiers;
      default:
        return state;
    }
  },

  salesSummary: (state = {}, action) => {
    switch (action.type) {
      case GET_SALES_SUCCESS:
        const summary = action.payload.summary;
        return summary;
      default:
        return state;
    }
  },
  salesTransactions: (state = {}, action) => {
    switch (action.type) {
      case GET_SALES_SUCCESS:
        const sales = action.payload.sales;
        return sales;
      case SALES_NEXT_PAGE_SUCCESS:
        const salesList = isEmpty(state)
          ? [...action.payload.sales]
          : [...state, ...action.payload.sales];
        return salesList;
      default:
        return state;
    }
  },
  groupedSales: (state = [], action) => {
    switch (action.type) {
      case GET_SALES_SUCCESS:
        const groupedSales = action.payload.grouped_sales || [];
        return groupedSales;
      default:
        return state;
    }
  },
  statsSummary: (state = {}, action) => {
    switch (action.type) {
      case GET_STATS_SUCCESS:
        const statsSummary = action.payload.users_stats;
        return statsSummary;
      default:
        return state;
    }
  },
  allCsvSummary: (state = {}, action) => {
    switch (action.type) {
      case DOWNLOAD_ALL_CSV_SUCCESS:
        const allCsvSummary = action.payload;
        return allCsvSummary;
      default:
        return state;
    }
  },
  isLoadingCsv: (state = false, action) => {
    switch (action.type) {
      case DOWNLOAD_ALL_CSV_REQUEST:
        return true;
      case DOWNLOAD_ALL_CSV_SUCCESS:
      case DOWNLOAD_ALL_CSV_FAILURE:
        return false;
      default:
        return false;
    }
  },
  isLoading: (state = false, action) => {
    switch (action.type) {
      case GET_STATS_REQUEST:
      case PUT_PAYOUTS_METHOD_REQUEST:
      case GET_SALES_REQUEST:
      case GET_BALANCE_REQUEST:
      case TIPSTER_FEE_TIERS_REQUEST:
        return true;
      case GET_STATS_SUCCESS:
      case GET_STATS_FAILURE:
      case GET_SALES_SUCCESS:
      case GET_BALANCE_SUCCESS:
      case TIPSTER_FEE_TIERS_SUCCESS:
      case TIPSTER_FEE_TIERS_FAILURE:
      case PUT_PAYOUTS_METHOD_SUCCESS:
        return false;
      default:
        return state;
    }
  },
  error: (state = null, action) => {
    switch (action.type) {
      case GET_STATS_FAILURE:
      case DOWNLOAD_ALL_CSV_FAILURE:
      case TIPSTER_FEE_TIERS_FAILURE:
        return action.payload;
      default:
        return null;
    }
  },
});
